import { CommonModule } from '@angular/common';
import { Component, EnvironmentInjector, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  AlertButton,
  AlertController,
  IonicModule,
  IonInput,
  ModalController,
  NavController,
  PopoverController,
} from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { EditAccountComponent } from 'src/app/accounts/edit-account/edit-account.component';
import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { CustomFieldsModule } from 'src/app/components/custom-fields/custom-fields.module';
import { ResponseInputModule } from 'src/app/components/custom-fields/response-input/response-input.module';
import { INBOX_TYPES, PRIVATE_CHANNEL_NAME, routes } from 'src/app/constants';
import { getSlackLink, removeUndefinedFields } from 'src/app/utils/utils';
import { Customer, Tag } from 'src/models';
import { Account } from 'src/models/accounts';
import { CHANNEL_TYPES } from 'src/models/channels';
import { Project, PROJECT_VISIBILITY } from 'src/models/projects';
import { AccountsService } from 'src/services/accounts.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TagsService } from 'src/services/tags.service';

import { EditEmailChannelComponent } from '../email/edit-email-channel.component';
import { EditMsTeamsChannelComponent } from '../ms-teams/edit-ms-teams-channel.component';
import { EditWidgetChannelComponent } from '../widget/edit-widget-channel.component';
@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxTippyModule,
    ResponseInputModule,
    CustomFieldsModule,
    RouterModule,
  ],
  selector: 'app-channel-admin',
  templateUrl: './channel-admin.page.html',
  styleUrls: ['./channel-admin.page.scss'],
})
export class ChannelAdminPage {
  @ViewChild('channelNameInput', { static: false }) channelNameInput: IonInput;

  public appPages;
  public channelId;
  public channel: Customer;
  public project: Partial<Project>;
  public accounts: Partial<Account>[];

  CHANNEL_TYPES = CHANNEL_TYPES;
  PROJECT_VISIBILITY = PROJECT_VISIBILITY;
  PRIVATE_CHANNEL_NAME = PRIVATE_CHANNEL_NAME;

  constructor(
    route: ActivatedRoute,
    public authService: AuthService,
    private customersService: CustomersService,
    private searchablePopoverService: SearchablePopoverService,
    private tagsService: TagsService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private navCtrl: NavController,
    private modalCtrl: ModalController,
    private accountsService: AccountsService,
    public environmentInjector: EnvironmentInjector,
  ) {
    this.appPages = [];
    this.channelId = route.snapshot.paramMap.get('channelId');

    if (!this.channelId && this.authService.projects?.length === 1) {
      this.project = this.authService.projects[0];
    }

    if (this.channelId) {
      this.getChannel();
    } else {
      setTimeout(() => {
        this.channelNameInput.setFocus();
      }, 500);
    }
  }

  async getChannel() {
    this.customersService.get(this.channelId).then(async (channel: Customer) => {
      this.channel = channel;
      this.appPages = [
        {
          title: 'Overview',
          url: `/dashboard/channels/${this.channelId}/overview`,
          icon: 'pulse',
        },
      ];

      if (this.channel.type === CHANNEL_TYPES.SLACK) {
        this.appPages.push(
          {
            title: 'Members',
            url: `/dashboard/channels/${this.channelId}/members`,
            icon: 'people',
          },
          {
            title: 'Settings',
            url: `/dashboard/channels/${this.channelId}/settings`,
            icon: 'cog',
          },
        );
      }

      await this.getAccounts();
    });
  }

  async getAccounts() {
    if (!this.channel?.slackChannel) {
      return;
    }

    const result = await this.accountsService.list({
      limit: 5,
      select: ['id', 'name', 'customFields', 'externalCrmMetadata', 'emailsAndDomains'],
      where: [
        {
          field: 'slackChannelIds',
          operator: 'contains',
          value: [this.channel.slackChannel.id],
        },
      ],
    });
    this.accounts = result.data;
  }

  async editAccount(accountId: string) {
    const modal = await this.modalCtrl.create({
      component: EditAccountComponent,
      componentProps: {
        id: accountId,
      },
      cssClass: 'modal-medium',
    });

    await modal.present();

    modal.onDidDismiss().then(() => {
      this.getAccounts();
    });
  }

  async saveEditedName() {
    try {
      await this.customersService.update(this.channelId, {
        name: this.channel.name,
      });
      this.msgSrvc.show('Updated');
    } catch (e) {
      this.msgSrvc.showError(e);
    }
  }

  async editTag(tag: Tag) {
    const options: ActionDropdownType[] = [
      {
        label: 'Remove tag',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      componentProps: {
        options,
        callback: async (_idx: number) => {
          popover.dismiss();

          await this.tagsService.deleteCustomerLinks(tag.id, [this.channelId]);

          this.getChannel();
        },
      },
    });
    popover.present();
  }

  addTag() {
    this.searchablePopoverService.tags({
      event,
      projectId: this.channel?.projectId ?? this.project?.id,
      callback: async (id: string) => {
        await this.tagsService.createCustomerLinks(id, [this.channelId]);

        this.getChannel();
      },
    });
  }

  openSlackChannel() {
    const slackChannel = this.getSlackChannel();

    if (!slackChannel) {
      return;
    }

    window.open(
      getSlackLink(slackChannel.teamId ?? this.authService.tenant.slackTeamId, null, null, slackChannel.id),
      '_blank',
    );
  }

  viewInbox() {
    const filter = [
      [
        { label: 'Channel', id: 'customerId', icon: 'chatbubbles', type: 'select' },
        [{ id: this.channelId, label: this.channel.name }],
      ],
    ];
    this.navCtrl.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.ALL}`, {
      queryParams: {
        filters: JSON.stringify(filter),
      },
      queryParamsHandling: 'merge',
    });
  }

  composeSlackBroadcast() {
    this.navCtrl.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: removeUndefinedFields({
        customerIds: this.channelId,
      }),
    });
  }

  async delete() {
    const subHeader = "We'll disconnect this channel, though conversations will still be visible in the dashboard";
    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Do it',
          handler: async () => {
            try {
              await this.customersService.delete(this.channelId);
              this.navCtrl.navigateRoot(`${routes.DASHBOARD}/channels`);
            } catch (e) {
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async showOptions() {
    const DELETE_LABEL = 'Disconnect';
    const RENAME_LABEL = 'Rename';

    const options: ActionDropdownType[] = [
      {
        label: RENAME_LABEL,
        icon: 'create-outline',
      },
      {
        label: DELETE_LABEL,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_index: number, label: string) => {
          popover.dismiss();

          if (label === DELETE_LABEL) {
            this.delete();
          } else if (label === RENAME_LABEL) {
            this.channelNameInput.setFocus();
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async save() {
    const updates: Partial<Customer> = removeUndefinedFields({
      projectId: this.project?.id,
    });

    try {
      await this.customersService.update(this.channelId, updates);

      await this.getChannel();

      this.msgSrvc.show('✔︎ Updated');
    } catch (err) {
      console.log(err);

      this.msgSrvc.show('Something went wrong');
    }
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: !this.channelId,
      selectedProjectId: this.project?.id ?? this.channel?.projectId,
      callback: async (id: string, _name: string, object: any) => {
        // Don't show warning if it's a new customer or the project didn't change
        if (!this.channelId || (this.channel && id === this.channel.projectId)) {
          this.project = object;

          if (this.channelId) {
            this.save();
          }

          return;
        }

        // Show warning before changing project
        const alert = await this.alertCtrl.create({
          header: 'Change Project?',
          message:
            "Changing the project will update all conversations associated with this channel. Tags from the previous project that don't exist in the new project will be removed from these conversations.",
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Change Project',
              handler: () => {
                this.project = object;

                if (this.channelId) {
                  this.save();
                }
              },
            },
          ],
        });

        await alert.present();
      },
    });
  }

  async editEmailConfiguration() {
    const modal = await this.modalCtrl.create({
      component: EditEmailChannelComponent,
      componentProps: {
        customerId: this.channel.id,
      },
      cssClass: 'modal-medium',
    });

    modal.onDidDismiss().then(() => {
      this.getChannel();
    });

    await modal.present();
  }

  async editInAppChatConfiguration() {
    const modal = await this.modalCtrl.create({
      component: EditWidgetChannelComponent,
      componentProps: {
        customerId: this.channel.id,
      },
      cssClass: 'modal-large',
    });

    modal.onDidDismiss().then(() => {
      this.getChannel();
    });

    await modal.present();
  }

  async editMicrosoftTeamsConfiguration() {
    const modal = await this.modalCtrl.create({
      component: EditMsTeamsChannelComponent,
      componentProps: {
        customerId: this.channel.id,
      },
      cssClass: 'modal-medium',
    });

    modal.onDidDismiss().then(() => {
      this.getChannel();
    });

    await modal.present();
  }

  async changeCustomerType() {
    const header = this.channel.isInternal ? 'Internal channel' : 'Shared channel';
    const subHeader = this.channel.isInternal
      ? 'This is an internal support channel for members of your Slack workspace.\n\nYou can change this to be a shared channel with external customers.'
      : 'This is an shared support channel for external customers or vendors.\n\nYou can change this to be an internal support channel for your own team.';

    const buttons: (string | AlertButton)[] = [
      {
        text: 'Dismiss',
      },
    ];

    if (this.channel.isInternal) {
      buttons.push({
        text: 'Switch to External',
        handler: () => {
          this.channel.isInternal = false;
          this.save();
        },
      });
    } else {
      buttons.push({
        text: 'Switch to Internal',
        handler: () => {
          this.channel.isInternal = true;
          this.save();
        },
      });
    }

    const alert = await this.alertCtrl.create({
      header,
      subHeader,
      buttons,
    });
    alert.present();
  }

  getSlackChannel() {
    if (this.channel.type === CHANNEL_TYPES.SLACK) {
      return this.channel.slackChannel;
    } else if (this.channel.type === CHANNEL_TYPES.MICROSOFT_TEAMS) {
      return this.channel.externalChatConnection.slackChannel;
    } else if (this.channel.type === CHANNEL_TYPES.WIDGET) {
      return this.channel.widget.slackChannel;
    } else if (this.channel.type === CHANNEL_TYPES.EMAIL) {
      return this.channel.inboundEmailRule.slackChannel;
    }

    return null;
  }
}
